import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "~/contexts/auth-context";
import { FirestoreOrganisationMarketplace } from "reedy-data/models";
import { MAX_KEYWORDS_CHARACTERS_COUNT } from "~/shared/constants";
import { toast } from "~/utils/toast";

/**
 * Fires the recommendation toast once when the relevant character counter
 * crosses `MAX_KEYWORDS_CHARACTERS_COUNT` upwards.
 *
 * The 500-char cap is only enforced server-side during keyword generation;
 * for manual edits we just nudge the user. The counters watched here are the
 * authoritative ones rendered on the Final List tabs (kept in lockstep with
 * `subjects.final` and `subjects.finalWords` from Firestore), so we never
 * have to recompute or tokenize on the client.
 *
 * - DE marketplace → watch the phrase character count (`subjects.final`).
 * - US/UK / non-DE → watch the derived single-word character count
 *   (`subjects.finalWords`), which the backend updates after each save.
 */
export const useKeywordLimitRecommendationToast = ({
  finalKeywordsCharacters,
  finalWordsCharacters,
}: {
  finalKeywordsCharacters: number;
  finalWordsCharacters: number;
}) => {
  const { t } = useTranslation(["components"]);
  const { organisation } = useSession();

  const isGermanMarketplace =
    organisation.settings?.marketplace === FirestoreOrganisationMarketplace.GER;

  const watched = isGermanMarketplace
    ? finalKeywordsCharacters
    : finalWordsCharacters;

  const previousRef = useRef<number | null>(null);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = watched;

    if (previous === null) {
      return;
    }

    if (
      watched > MAX_KEYWORDS_CHARACTERS_COUNT &&
      previous <= MAX_KEYWORDS_CHARACTERS_COUNT
    ) {
      toast.info(
        t(
          "components:keywordsTab.finalKeywords.toasts.info.keywordLimitRecommendation",
        ),
      );
    }
  }, [watched, t]);
};
